import React, { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import {
  Card,
  Button,
  Badge,
  SearchInput,
  Select,
  Spinner,
  EmptyState,
  Modal,
  Tooltip,
} from "../../components/UI";
import { followUpsAPI } from "../../services/api";
import toast from "react-hot-toast";
import {
  HiOutlineExclamation,
  HiOutlineClock,
  HiOutlineCalendar,
  HiOutlineRefresh,
  HiOutlineExternalLink,
  HiOutlineCheckCircle,
  HiOutlineArrowSmUp,
  HiOutlineFilter,
  HiOutlineUser,
  HiOutlinePhone,
} from "react-icons/hi";
import { format, formatDistanceToNow, parseISO } from "date-fns";

const EscalatedFollowUps = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // escalated, resolved
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);

  // Complete follow-up modal
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);

  // Fetch escalated follow-ups
  const { data: escalatedData, isLoading, refetch } = useQuery({
    queryKey: ["escalatedFollowUps", page, pageSize, search, statusFilter],
    queryFn: async () => {
      const params = {
        page,
        limit: pageSize,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
      };
      const response = await followUpsAPI.getEscalated(params);
      return response.data?.data || response.data || {};
    },
    // Keep the current list on screen while a new search/filter loads, so the
    // full-page spinner doesn't unmount the search box mid-typing.
    placeholderData: keepPreviousData,
  });

  // Complete follow-up mutation
  const completeMutation = useMutation({
    mutationFn: ({ id, data }) => followUpsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["escalatedFollowUps"]);
      toast.success("Follow-up marked as completed");
      setShowCompleteModal(false);
      setSelectedFollowUp(null);
    },
    onError: () => toast.error("Failed to update follow-up"),
  });

  // Manually escalate a follow-up
  const escalateMutation = useMutation({
    mutationFn: (id) => followUpsAPI.escalate(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["escalatedFollowUps"]);
      toast.success("Follow-up escalated successfully");
    },
    onError: () => toast.error("Failed to escalate follow-up"),
  });

  const followUps = escalatedData?.data || escalatedData?.followUps || [];
  const pagination = escalatedData?.pagination || {};
  const totalPages = Math.ceil((pagination.total || followUps.length) / pageSize);

  const handleComplete = (followUp) => {
    setSelectedFollowUp(followUp);
    setShowCompleteModal(true);
  };

  const confirmComplete = () => {
    if (selectedFollowUp) {
      completeMutation.mutate({
        id: selectedFollowUp._id,
        data: { status: "completed" },
      });
    }
  };

  const getOverdueLabel = (scheduledDate) => {
    try {
      return formatDistanceToNow(parseISO(scheduledDate), { addSuffix: true });
    } catch {
      return "Overdue";
    }
  };

  const getOverdueDays = (scheduledDate) => {
    const now = new Date();
    const scheduled = new Date(scheduledDate);
    const diffMs = now - scheduled;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day overdue";
    return `${diffDays} days overdue`;
  };

  const getOverdueSeverity = (scheduledDate) => {
    const now = new Date();
    const scheduled = new Date(scheduledDate);
    const diffHours = (now - scheduled) / (1000 * 60 * 60);
    if (diffHours < 24) return "warning"; // yellow
    if (diffHours < 72) return "danger"; // orange
    return "critical"; // red
  };

  const severityStyles = {
    warning: "bg-yellow-50 text-yellow-700 border-yellow-200",
    danger: "bg-orange-50 text-orange-700 border-orange-200",
    critical: "bg-red-50 text-red-700 border-red-200",
  };

  const severityBadgeStyles = {
    warning: "bg-yellow-100 text-yellow-800",
    danger: "bg-orange-100 text-orange-800",
    critical: "bg-red-100 text-red-800",
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 flex items-center gap-2">
              <HiOutlineExclamation className="w-7 h-7 text-red-500" />
              Escalated Follow-ups
            </h1>
            <p className="text-sm text-secondary-500 mt-1">
              Overdue follow-ups that have been escalated to managers
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="gap-1.5"
            >
              <HiOutlineRefresh className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <HiOutlineExclamation className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary-900">
                  {followUps.filter((f) => f.status === "pending").length}
                </p>
                <p className="text-xs text-secondary-500">Pending Escalations</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <HiOutlineClock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary-900">
                  {followUps.filter((f) => {
                    if (!f.scheduledDate) return false;
                    const diff = (new Date() - new Date(f.scheduledDate)) / (1000 * 60 * 60);
                    return diff > 72;
                  }).length}
                </p>
                <p className="text-xs text-secondary-500">Critical (3+ days)</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <HiOutlineCheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary-900">
                  {pagination.total || followUps.length}
                </p>
                <p className="text-xs text-secondary-500">Total Escalated</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex-1 w-full sm:w-auto">
              <SearchInput
                value={search}
                onChange={(value) => { setSearch(value); setPage(1); }}
                placeholder="Search by lead name, phone, or telecaller..."
                className="w-full"
              />
            </div>
            <Select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full sm:w-40"
              options={[
                { value: '', label: 'All Status' },
                { value: 'pending', label: 'Pending' },
                { value: 'completed', label: 'Resolved' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
          </div>
        </Card>

        {/* Follow-ups List */}
        {followUps.length === 0 ? (
          <Card className="p-12">
            <EmptyState
              icon={<HiOutlineExclamation className="w-12 h-12" />}
              title="No escalated follow-ups"
              description="When follow-ups become overdue, they'll be escalated and appear here."
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {followUps.map((fu) => {
              const severity = getOverdueSeverity(fu.scheduledDate);
              const severityStyle = severityStyles[severity];
              const badgeStyle = severityBadgeStyles[severity];

              return (
                <Card key={fu._id} className={`overflow-hidden border-l-4 ${severity === "critical" ? "border-l-red-500" : severity === "danger" ? "border-l-orange-500" : "border-l-yellow-500"}`}>
                  <div className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      {/* Left: Follow-up info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Lead name */}
                          <button
                            onClick={() => navigate(`/app/leads/${fu.leadId?._id || fu.leadId}`)}
                            className="text-sm font-semibold text-primary-700 hover:text-primary-800 hover:underline truncate max-w-[200px]"
                          >
                            {fu.leadId?.name || "Unknown Lead"}
                          </button>
                          {/* Severity badge */}
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badgeStyle}`}>
                            {getOverdueDays(fu.scheduledDate)}
                          </span>
                          {/* Status badge */}
                          {fu.status === "pending" && (
                            <Badge variant="warning" size="sm">Pending</Badge>
                          )}
                          {fu.status === "completed" && (
                            <Badge variant="success" size="sm">Completed</Badge>
                          )}
                          {fu.status === "cancelled" && (
                            <Badge variant="default" size="sm">Cancelled</Badge>
                          )}
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-secondary-500">
                          {/* Telecaller */}
                          {fu.assignedTo && (
                            <span className="flex items-center gap-1">
                              <HiOutlineUser className="w-3.5 h-3.5" />
                              {fu.assignedTo.firstName
                                ? `${fu.assignedTo.firstName} ${fu.assignedTo.lastName || ""}`
                                : fu.assignedTo.email || "Unknown"}
                            </span>
                          )}
                          {/* Phone */}
                          {fu.leadId?.phone && (
                            <span className="flex items-center gap-1">
                              <HiOutlinePhone className="w-3.5 h-3.5" />
                              {fu.leadId.phone}
                            </span>
                          )}
                          {/* Scheduled date */}
                          <span className="flex items-center gap-1">
                            <HiOutlineCalendar className="w-3.5 h-3.5" />
                            Scheduled: {format(new Date(fu.scheduledDate), "MMM d, yyyy h:mm a")}
                          </span>
                          {/* Escalated date */}
                          {fu.escalatedAt && (
                            <span className="flex items-center gap-1 text-red-600">
                              <HiOutlineExclamation className="w-3.5 h-3.5" />
                              Escalated: {format(new Date(fu.escalatedAt), "MMM d, yyyy h:mm a")}
                            </span>
                          )}
                        </div>

                        {/* Follow-up type & notes */}
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          {fu.type && (
                            <Badge variant="default" size="sm">{fu.type}</Badge>
                          )}
                          {fu.notes && (
                            <span className="text-secondary-500 truncate max-w-[300px]">
                              {fu.notes}
                            </span>
                          )}
                        </div>

                        {/* Escalated to (managers) */}
                        {fu.escalatedTo && fu.escalatedTo.length > 0 && (
                          <div className="mt-2 text-xs text-secondary-400">
                            Notified: {fu.escalatedTo.map((m) =>
                              m.firstName ? `${m.firstName} ${m.lastName || ""}` : m.email || "Manager"
                            ).join(", ")}
                          </div>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Tooltip content="View Lead">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/app/leads/${fu.leadId?._id || fu.leadId}`)}
                            className="gap-1"
                          >
                            <HiOutlineExternalLink className="w-4 h-4" />
                            Lead
                          </Button>
                        </Tooltip>
                        {fu.status === "pending" && (
                          <>
                            <Tooltip content="Mark as Completed">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleComplete(fu)}
                                className="gap-1"
                              >
                                <HiOutlineCheckCircle className="w-4 h-4" />
                                Complete
                              </Button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-secondary-500">
              Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, pagination.total || followUps.length)} of {pagination.total || followUps.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-secondary-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Complete Follow-up Modal */}
        {showCompleteModal && selectedFollowUp && (
          <Modal
            isOpen={showCompleteModal}
            onClose={() => { setShowCompleteModal(false); setSelectedFollowUp(null); }}
            title="Mark Follow-up as Completed"
          >
            <div className="space-y-4">
              <p className="text-sm text-secondary-600">
                Are you sure you want to mark this follow-up as completed?
              </p>
              <div className="bg-secondary-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="font-medium">Lead:</span> {selectedFollowUp.leadId?.name || "Unknown"}</p>
                <p><span className="font-medium">Scheduled:</span> {selectedFollowUp.scheduledDate ? format(new Date(selectedFollowUp.scheduledDate), "MMM d, yyyy h:mm a") : "N/A"}</p>
                <p><span className="font-medium">Type:</span> {selectedFollowUp.type || "N/A"}</p>
                {selectedFollowUp.notes && (
                  <p><span className="font-medium">Notes:</span> {selectedFollowUp.notes}</p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setShowCompleteModal(false); setSelectedFollowUp(null); }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={confirmComplete}
                  disabled={completeMutation.isPending}
                >
                  {completeMutation.isPending ? "Marking..." : "Mark Completed"}
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </Layout>
  );
};

export default EscalatedFollowUps;